import React from 'react'
import { Box, Button, Modal } from '@mui/material'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { AvailabilityCheckSearchForm } from 'types/interfaces'
import Header from 'components/Header'
import AdminAvailabilityCheckTable from 'components/AdminAvailabilityCheckTable'
import AvailabilityCheckModal from 'components/AvailabilityCheckModal'
import AvailabilityCheck from 'logic/AvailabilityCheck'
import update from 'immutability-helper'
import { Subject } from 'rxjs'
import { useNavigate } from 'react-router'
import { adminAvailabilityCheckDetailURL } from 'routes/urls'

const AdminAvailabilityCheckList: React.FC = () => {
    const { api } = useAPI()
    const navigate = useNavigate()
    const { showAlert } = useSnackBarAlert()
    const [form, setForm] = React.useState<AvailabilityCheckSearchForm>({ sideloads: ['num_results'] })
    const [open, setOpen] = React.useState<boolean>(false)
    const reload$ = React.useMemo(() => new Subject<boolean>(), [])

    const onCreate = React.useCallback(() => {
        setOpen(true)
    }, [setOpen])

    const onDelete = React.useCallback(async (check: AvailabilityCheck) => {
        try {
            await api.deleteAvailabilityCheck(check.id)
            reload$.next(true)
            showAlert('success', 'Availability check deleted successfully')
        } catch (error) {
            showAlert('error', 'Failed to delete availability check')
            console.error(error)
        }
    }, [api, reload$, showAlert])

    const updateForm = React.useCallback((name: string, value: any) => {
        setForm(update(form, { [name]: { $set: value } }))
    }, [form])

    const handleCreateSuccess = React.useCallback(async () => {
        setOpen(false)
        reload$.next(true)
        showAlert('success', 'Availability check created successfully')
    }, [reload$, showAlert])

    const handleCreateCancel = React.useCallback(() => {
        setOpen(false)
    }, [])

    return (
        <React.Fragment>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                <Header text='Availability Checks'>
                    <Button variant='contained' color='primary' onClick={onCreate}>Create Availability Check</Button>
                </Header>
                <AdminAvailabilityCheckTable
                    reload$={reload$}
                    form={form}
                    updateForm={updateForm}
                    onDelete={onDelete}
                    onRowClick={(params) => navigate(adminAvailabilityCheckDetailURL(params.row.id))}
                />
            </Box>
            <Modal open={open} onClose={handleCreateCancel}>
                <AvailabilityCheckModal
                    onSuccess={handleCreateSuccess}
                    onCancel={handleCreateCancel}
                />
            </Modal>
        </React.Fragment>
    )
}

export default AdminAvailabilityCheckList
